import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { DtoContactRequest, DtoFormData, Language } from '@common';
import { catchError, map, Observable, shareReplay, Subject, takeUntil, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ContactService, DtoContactFormData, FormDataService } from '../../../core';
import { MarkdownPipe } from '../../../shared';

@Component({
  selector: 'app-contact-form',
  imports: [
    CommonModule,
    FormsModule,
    MarkdownPipe,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    ReactiveFormsModule
  ],
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss']
})
export class ContactFormComponent implements OnInit, OnDestroy {
  //#region @Input ------------------------------------------------------------
  @Input() currentLanguage!: Language;
  //#endregion ----------------------------------------------------------------

  //#region public properties -------------------------------------------------
  private readonly destroyed: Subject<void>;
  private readonly contactService: ContactService;
  private readonly router: Router;
  private readonly formDataService: FormDataService;
  //#endregion

  //#region public properties -------------------------------------------------
  public formGroup: UntypedFormGroup;
  public salutations: Array<string>;
  public isHandset: Observable<boolean>;
  public mailIsAvailable?: boolean | undefined;
  public error?: string;
  public formData!: DtoContactFormData;
  //#endregion

  //#region getters -----------------------------------------------------------
  public get salutationValues(): Array<string> {
    return this.formData?.salutationValues as Array<string>;
  }
  //#endregion
  //#region form controls -----------------------------------------------------
  public senderControl: UntypedFormControl;
  public senderRepeatControl: UntypedFormControl;
  public salutationControl: UntypedFormControl;
  public contentControl: UntypedFormControl;
  //#endregion

  //#region Constructor & C° --------------------------------------------------
  constructor(
    breakpointObserver: BreakpointObserver,
    formBuilder: UntypedFormBuilder,
    router: Router,
    contactService: ContactService,
    formDataService: FormDataService
  ) {
    this.contactService = contactService;
    this.formDataService = formDataService;
    this.router = router;
    this.error = undefined;
    this.destroyed = new Subject<void>();
    this.senderControl = new FormControl<string | undefined>(environment.contactFormEmail, [
      Validators.required,
      Validators.email
    ]);
    this.senderRepeatControl = new FormControl<string | undefined>(environment.contactFormEmail, [
      Validators.required,
      Validators.email
    ]);
    this.salutationControl = new FormControl<string | undefined>(
      environment.contactFormSalutation,
      Validators.required
    );
    this.contentControl = new FormControl<string | undefined>(environment.contactFormContent, [
      Validators.required,
      Validators.minLength(50)
    ]);

    this.formGroup = formBuilder.group({
      sender: this.senderControl,
      senderRepeat: this.senderRepeatControl,
      salutation: this.salutationControl,
      firstName: new FormControl<string | undefined>(environment.contactFormFirstname),
      lastName: new FormControl<string | undefined>(environment.contactFormLastname),
      perDu: new FormControl<boolean | undefined>(false),
      subject: new FormControl<string | undefined>(environment.contactFormSubject),
      content: this.contentControl,
      phoneNumber: new FormControl<string | undefined>(undefined)
    });

    this.formGroup.addValidators(this.matchEmails);
    this.mailIsAvailable = undefined;
    this.salutations = new Array<string>('Herr', 'Frau', 'ohne');
    this.isHandset = breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.XSmall]).pipe(
      takeUntil(this.destroyed),
      map((result: BreakpointState) => result.matches),
      shareReplay()
    );
  }

  ngOnInit(): void {
    this.contactService.checkMailStatus().subscribe((isAvailable: boolean) => (this.mailIsAvailable = isAvailable));
    this.formDataService
      .getContactFormData(this.currentLanguage)
      .subscribe((data: DtoFormData) => (this.formData = data.data));
    // this.formDataService
    //   .contactFormData
    //   .subscribe((data: DtoContactFormData) => this.formData = data);
    // this.formDataService.loadContactFormData().subscribe((data: DtoFormData) => this.formData = data.data);
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
  //#endregion

  //#region UI triggered methods ----------------------------------------------
  public clear(): void {
    Object.keys(this.formGroup.controls).forEach((key: string) => this.formGroup.controls[key].reset(undefined));
    this.error = undefined;
  }

  public send(): void {
    const request: DtoContactRequest = {
      sender: (this.formGroup.controls['sender'] as FormControl<string | undefined>)?.value || '',
      senderRepeat: (this.formGroup.controls['senderRepeat'] as FormControl<string | undefined>)?.value || '',
      salutation: (this.formGroup.controls['salutation'] as FormControl<string | undefined>)?.value || '',
      firstName: (this.formGroup.controls['firstName'] as FormControl<string | undefined>)?.value || '',
      lastName: (this.formGroup.controls['lastName'] as FormControl<string | undefined>)?.value || '',
      subject: (this.formGroup.controls['subject'] as FormControl<string | undefined>)?.value || '',
      content: (this.formGroup.controls['content'] as FormControl<string | undefined>)?.value || '',
      perDu: (this.formGroup.controls['perDu'] as FormControl<boolean | undefined>)?.value || false
    };
    this.contactService
      .sendMail(request)
      .pipe(
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError((_error: any) => {
          this.setError();
          return throwError(() => new Error(''));
        })
      )
      .subscribe((response: number) => {
        if (response < 400) {
          this.error = undefined;
          void this.router.navigate(['page'], { queryParams: { content: 'more/contact-request-sent' } });
        } else {
          this.setError();
        }
      });
  }

  private setError(): void {
    this.error = this.formDataService.processErrorMessage;
  }

  private matchEmails(control: AbstractControl): ValidationErrors | null {
    const sender = (control.get('sender') as FormControl<string | undefined>)?.value;
    const repeat = (control.get('senderRepeat') as FormControl<string | undefined>)?.value;
    if (sender != repeat) {
      return { noMatch: true };
    }
    return null;
  }
  //#endregion
}
